// ./src/components/generic/ScanConsole/ScanConsole.tsx
//
// ROADMAP #167 (owner, 2026-10-16): every scanning page has the same visual pattern — the camera button in the page
// header or in the input, Back in the header and never as a link on the page (the owner's words are in
// `check-scan-pages`). ONE scanning layout for every page that scans — the guest door console
// (`EventCheckinPage`), the volunteer check-in, voucher redemption and the inventory
// operations console. Each page keeps its own logic (what a code means, what a scan does); THIS draws it:
//
//   header   Back · Camera · Refresh (desktop) · the page's own actions   ← `scanHeaderActions`, nowhere else
//   mode     the one control that changes what a scan MEANS                ← `ScanModeControl` (or the page's chips)
//   context  what the scan applies to (the date at the door)
//   status   what is being scanned + the session counters                  ← `ScanCounters`
//   input    "Scan or type a code…"                                         ← `ScanCodeInput`
//   camera   the viewfinder, with the mode in words under it                ← `ScanCamera`
//   banner   the result of the last scan                                    ← `ScanResultBanner`
//   error    the page's ErrorAlert
//   body     the card(s), the matches, the roster, the form, Recent         ← `ScanEmptyHint`, `ScanRecentList`
//
// ⚠ THE CAMERA BUTTON IS IN THE HEADER on every scanning page (the owner allowed the header OR the input — ONE was
//   chosen so the pages agree). The volunteer roster has no code box to put it in, so the header is the only place
//   all four share; and the header is fixed on a phone, so the button never scrolls away.
// ⚠ BACK IS A HEADER ACTION, never an icon or a link in the page body (the redeem page had its own).
// ⚠ Nothing here is small: inputs and the mode control are `md` on every screen (they were `sm` on a desktop — owner:
//   "segment controls should be medium size"), and the banner's words are `md` and up.
// ⚠⚠ THE CAMERA GETS THE SCREEN (owner, 2026-11-02: "see if we can save some vertical space when scanning with built-in
//   camera so user does not have to scroll"). On a phone, with the camera on (`focus`): the shell's event strip, tabs and
//   bottom bar step aside (`PageChrome focusMode`), the counters and the mode are drawn ON the picture, the code box is one
//   tap away on the picture, and only the body scrolls. Stopping the camera puts everything back.
// ▶ `check-scan-pages` (apps/app) fails a scanning page that draws its own camera, banner, back or mode control.
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Badge, Box, Divider, Group, Loader, Paper, ScrollArea, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { playScanFeedback, unlockScanSound } from "@em/ui";

import { useAppEnvironment } from "@/contexts";

import { AppIcon } from "../AppIcon";
import { QrScanner } from "../QrScanner";
import { PullToRefresh } from "../PullToRefresh";
import { ContentLoadingOverlay } from "../ContentLoadingOverlay";

export * from "./scanHeaderActions";

// ── The parts ─────────────────────────────────────────────────────────────────

export interface ScanModeOption {
  value: string;
  label: string;
}

export interface ScanModeControlProps {
  value: string;
  onChange: (value: string) => void;
  data: ScanModeOption[];
  /** The mode's colour — the same on every console: teal = arriving, blue = leaving, gray = only looking. */
  color: string;
}

/** The control that changes what a scan MEANS. Full width, `md` on every screen. */
export const ScanModeControl: React.FC<ScanModeControlProps> = ({ value, onChange, data, color }) => (
  <SegmentedControl fullWidth size="md" color={color} value={value} onChange={onChange} data={data} />
);

export interface ScanCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter — a handheld scanner types the code and presses it. */
  onSubmit: () => void;
  placeholder: string;
  /** A code is being looked up. */
  loading?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}

/**
 * The code box: a handheld scanner types into it, a person types a code (or a name, where the page searches).
 * ⚠ `md` everywhere — below 16px iOS Safari zooms the page on focus, mid-shift.
 */
export const ScanCodeInput = forwardRef<HTMLInputElement, ScanCodeInputProps>(
  ({ value, onChange, onSubmit, placeholder, loading, autoFocus, disabled }, ref) => (
    <TextInput
      ref={ref}
      size="md"
      value={value}
      onChange={(e) => {
        // ⓘ Read before anything is deferred (memory: no event read in a state updater).
        const next = e.currentTarget.value;

        onChange(next);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onSubmit();
        }
      }}
      autoFocus={autoFocus}
      disabled={disabled}
      leftSection={<AppIcon iconName="Scan" size={18} />}
      rightSection={loading ? <Loader size="xs" /> : null}
      placeholder={placeholder}
    />
  )
);

ScanCodeInput.displayName = "ScanCodeInput";

export interface ScanCameraProps {
  /** False releases the camera (a drawer over it, a create in progress). */
  active: boolean;
  onDecode: (text: string) => void;
  /** A just-scanned code is being looked up. */
  processing?: boolean;
  /** "wide" for 1D product barcodes (inventory); QR is the default. */
  guide?: "square" | "wide";
  /** "guide" (the default) reads only inside the frame — a shelf, or a queue of badges, has a code beside this one. */
  decodeArea?: "visible" | "guide";
  /** Override the viewfinder height (the inventory console keeps the form under it in view). */
  height?: number;
  /** The mode in words, on the viewfinder — the mode control is out of eye line once somebody aims at a badge. */
  caption?: { text: string; color: string } | null;
  /** The operator must finish something before the next code; the words say what. */
  paused?: string | null;
  /** A new value lets the same code be scanned again (the mode changed, the operator cancelled). */
  rearmKey?: string | number | null;
  /** How long a spent code must be out of sight. Omit where scanning is non-stop; shorter where each scan is confirmed. */
  sameCodeGoneMs?: number;
  /** ⚠ A new object is a new result, and sounds: a page building it in render must memoise it. */
  banner?: ScanBannerValue | null;
  /** Drawn on the viewfinder (the session's counters, while `focus` takes them off the page). */
  counters?: ScanCounter[] | null;
  /** The code box, one tap away, while `focus` takes it off the page. */
  typing?: { open: boolean; onToggle: () => void; label: string } | null;
}

export const ScanCamera: React.FC<ScanCameraProps> = ({
  active,
  onDecode,
  processing,
  guide,
  decodeArea,
  height,
  caption,
  paused,
  rearmKey,
  sameCodeGoneMs,
  banner,
  counters,
  typing,
}) => {
  const { isMobile } = useAppEnvironment();
  // ⚠ What was already on screen when the camera came on is not a new result: no sound, no flash.
  const startedWith = useRef(banner ?? null);
  const result = useMemo(() => (banner && banner !== startedWith.current ? { kind: banner.kind } : null), [banner]);

  useEffect(() => {
    if (result) {
      playScanFeedback(result.kind);
    }
  }, [result]);

  return (
    <QrScanner
      active={active}
      onDecode={onDecode}
      processing={processing}
      guide={guide}
      decodeArea={decodeArea ?? "guide"}
      // ⓘ Smaller while typing: what they typed for is listed below it, above the keyboard.
      height={height ?? (typing?.open ? "clamp(110px, 18vh, 160px)" : isMobile ? "clamp(200px, 34vh, 320px)" : 300)}
      paused={paused}
      rearmKey={rearmKey}
      sameCodeGoneMs={sameCodeGoneMs}
      result={result}
      soundSwitch
    >
      {counters && counters.length > 0 ? (
        <Box style={{ position: "absolute", top: 12, left: 56, right: 56, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <ScanCounters items={counters.map((c) => ({ ...c, variant: "white" }))} />
        </Box>
      ) : null}
      {caption ? (
        <Box style={{ position: "absolute", bottom: 10, left: 10, right: 56, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <Badge size="lg" color={caption.color} variant="filled" tt="none" style={{ maxWidth: "100%" }}>
            {caption.text}
          </Badge>
        </Box>
      ) : null}
      {typing ? (
        <ActionIcon
          variant="filled"
          color={typing.open ? "brand" : "dark"}
          radius="xl"
          size="lg"
          onClick={typing.onToggle}
          aria-pressed={typing.open}
          aria-label={typing.label}
          style={{ position: "absolute", bottom: 10, right: 10, opacity: 0.85 }}
        >
          <AppIcon iconName="Keyboard" size={18} />
        </ActionIcon>
      ) : null}
    </QrScanner>
  );
};

export type ScanBannerKind = "ok" | "warn" | "error" | "info";

export interface ScanBannerValue {
  kind: ScanBannerKind;
  title: string;
  /** Under the title; line breaks are kept. */
  detail?: string | null;
  /** Extra summary lines (an inventory op's quantity, location, lot). */
  lines?: string[];
  /** Sounds and colours the frame, and prints nothing: the card says it. ⚠ No banner at all would be a scan that worked in silence. */
  quiet?: boolean;
}

const BANNER_STYLE: Record<ScanBannerKind, { bg: string; border: string; ink: string; icon: string }> = {
  ok: { bg: "var(--mantine-color-teal-light)", border: "var(--mantine-color-teal-6)", ink: "var(--mantine-color-teal-9)", icon: "CircleCheck" },
  warn: { bg: "var(--mantine-color-orange-light)", border: "var(--mantine-color-orange-6)", ink: "var(--mantine-color-orange-9)", icon: "AlertTriangle" },
  error: { bg: "var(--mantine-color-red-light)", border: "var(--mantine-color-red-6)", ink: "var(--mantine-color-red-9)", icon: "CircleX" },
  info: { bg: "var(--mantine-color-blue-light)", border: "var(--mantine-color-blue-6)", ink: "var(--mantine-color-blue-9)", icon: "InfoCircle" },
};

export interface ScanResultBannerProps {
  banner: ScanBannerValue;
  /** A button the result offers (Admit anyway, Create item from this code). */
  action?: React.ReactNode;
  onClose?: () => void;
  /**
   * One tighter block — for the inventory console, whose banner is pinned with the code box and must leave the form
   * under it on screen. Same colours, icon and edge; less padding.
   */
  compact?: boolean;
  /** Less padding, every word kept: a phone's console, with the camera taking the screen. */
  dense?: boolean;
}

/**
 * The result of the last scan — big, coloured, and never a toast (a toast sits over the camera and the next scan).
 * ⚠ ONE look on every console: the colour is the first thing door crew read, before the words.
 */
export const ScanResultBanner: React.FC<ScanResultBannerProps> = ({ banner, action, onClose, compact = false, dense = false }) => {
  const { t } = useTranslation(["common"]);
  const s = BANNER_STYLE[banner.kind];

  return (
    <Paper radius="md" p={compact || dense ? "xs" : "md"} style={{ background: s.bg, borderLeft: `4px solid ${s.border}` }}>
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Box pt={compact ? 2 : 0} style={{ flexShrink: 0 }}>
          <AppIcon iconName={s.icon} size={compact ? 22 : 28} color={s.border} />
        </Box>
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text fw={700} size={compact ? "md" : "lg"} c={s.ink} style={{ lineHeight: 1.25 }} lineClamp={compact ? 2 : undefined}>
            {banner.title}
          </Text>
          {banner.detail ? (
            <Text size="md" c={s.ink} style={{ opacity: 0.9, whiteSpace: "pre-line" }} lineClamp={compact ? 2 : undefined}>
              {banner.detail}
            </Text>
          ) : null}
          {banner.lines && banner.lines.length > 0 ? (
            <Text size="md" c={s.ink} style={{ opacity: 0.9 }} lineClamp={compact ? 1 : undefined}>
              {banner.lines.join(" · ")}
            </Text>
          ) : null}
          {action ? <Box mt="xs">{action}</Box> : null}
        </Box>
        {onClose ? (
          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label={t("common:buttons.close", { defaultValue: "Close" })}>
            <AppIcon iconName="X" size={18} />
          </ActionIcon>
        ) : null}
      </Group>
    </Paper>
  );
};

/** What the page looks like before the first scan. */
export const ScanEmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <Paper withBorder radius="md" p="lg">
    <Stack align="center" gap={6}>
      <AppIcon iconName="Scan" size={30} color="var(--mantine-color-dimmed)" />
      <Text size="md" c="dimmed" ta="center">
        {text}
      </Text>
    </Stack>
  </Paper>
);

export interface ScanCounter {
  key: string;
  label: string;
  color: string;
  variant?: "light" | "outline" | "white";
}

/** The session's tally (In / Out, Redeemed) and markers such as "Test event". */
export const ScanCounters: React.FC<{ items: ScanCounter[] }> = ({ items }) => (
  <Group gap={6} wrap="wrap">
    {items.map((c) => (
      <Badge key={c.key} size="lg" color={c.color} variant={c.variant ?? "light"}>
        {c.label}
      </Badge>
    ))}
  </Group>
);

export interface ScanRecentEntry {
  key: string;
  /** What happened — Checked in, Already in, Redeemed. */
  label: string;
  color: string;
  /** Who or what. */
  name: string;
  /** #176 — where it happened (the service point the scan recorded); omit when none was. */
  where?: string | null;
  /** Already formatted. */
  at: string;
}

/** The last few scans on this device, newest first. */
export const ScanRecentList: React.FC<{ title: string; entries: ScanRecentEntry[] }> = ({ title, entries }) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <>
      {/* ⓘ Mantine draws a divider's label at xs — the heading of the list is text, so it says md. */}
      <Divider
        label={
          <Text size="md" c="dimmed" fw={600}>
            {title}
          </Text>
        }
        labelPosition="left"
        mt="xs"
      />
      <Stack gap={6}>
        {entries.map((r) => (
          <Group key={r.key} gap="xs" justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
              <Badge size="md" color={r.color} variant="light" style={{ flexShrink: 0 }}>
                {r.label}
              </Badge>
              <Text size="md" truncate>
                {r.name}
                {r.where ? (
                  <Text span size="md" c="dimmed">
                    {` · ${r.where}`}
                  </Text>
                ) : null}
              </Text>
            </Group>
            <Text size="md" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {r.at}
            </Text>
          </Group>
        ))}
      </Stack>
    </>
  );
};

// ── The layout ────────────────────────────────────────────────────────────────

export interface ScanConsoleProps {
  /** `ScanModeControl` — or the page's own chips when it has too many modes for one row (inventory). */
  mode?: React.ReactNode;
  /** What the scan applies to (the door's date). */
  context?: React.ReactNode;
  /** What is being scanned, in words. */
  status?: React.ReactNode;
  /** The session's tally: beside `status`, or on the picture while `focus`. */
  counters?: ScanCounter[];
  /** `ScanCodeInput` — omit on a page with no code box (the volunteer roster scans by camera only). */
  input?: React.ReactNode;
  /** The header camera's state (`scanHeaderActions`). */
  scanning: boolean;
  camera: Omit<ScanCameraProps, "active" | "banner" | "counters" | "typing"> & { active?: boolean };
  banner?: ScanBannerValue | null;
  bannerAction?: React.ReactNode;
  onBannerClose?: () => void;
  compactBanner?: boolean;
  /** The page's `ErrorAlert`. */
  error?: React.ReactNode;
  /**
   * "console" — the page is one screen: the scan bar stays put and only the body scrolls (with the pull gesture and
   * the refresh bar over it). "flow" — the page scrolls as a whole (the volunteer roster, the inventory form).
   */
  layout: "console" | "flow";
  /** "console": the body's pull-to-refresh and its progress bar. */
  onRefresh?: () => Promise<void>;
  refreshing?: boolean;
  /**
   * "flow": pin the code box, camera and banner under the header while the page scrolls (the inventory form — #65 c).
   * ⚠ Pass false while a field below has focus on a phone: the keyboard is up and the pinned bar would cover it.
   */
  pinScanBar?: boolean;
  /** The pinned bar's top on a phone (the shell's fixed header); 0 on wider screens. */
  pinTop?: number;
  /** `ShortcutHintBar`. */
  hints?: React.ReactNode;
  /**
   * The camera takes the screen. Defaults to a phone with the camera on; the page's `PageChrome focusMode` says the same.
   * False on the inventory console: its form is the work, and the code box stays.
   */
  focus?: boolean;
  children?: React.ReactNode;
}

/** Room kept for what a scan brought up, in the camera-first layout: one person's card and its buttons. */
const FOCUSED_BODY_MIN = 240;

/** The room from `ref` to the bottom of the window, while `enabled`: the shell's own wrappers have no height to inherit. */
const useFitToWindow = (enabled: boolean) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setHeight(null);

      return undefined;
    }

    // ⚠ The VISUAL viewport: the keyboard covers `innerHeight` without changing it on iOS, and what was listed went under it.
    const viewport = window.visualViewport;
    const measure = () => {
      const top = ref.current?.getBoundingClientRect().top;

      if (top !== undefined) {
        const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;

        // ⓘ Less the page's own bottom padding (`md`).
        setHeight(Math.max(320, visibleBottom - top - 16));
      }
    };

    window.scrollTo(0, 0);
    measure();

    // The shell's strip and tabs leave in the same commit, or the next: measure again once they have.
    const frame = requestAnimationFrame(measure);

    window.addEventListener("resize", measure);
    viewport?.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      viewport?.removeEventListener("resize", measure);
    };
  }, [enabled]);

  return { ref, height };
};

export const ScanConsole: React.FC<ScanConsoleProps> = ({
  mode,
  context,
  status,
  counters,
  input,
  scanning,
  camera,
  banner,
  bannerAction,
  onBannerClose,
  compactBanner,
  error,
  layout,
  onRefresh,
  refreshing = false,
  pinScanBar = false,
  pinTop = 0,
  hints,
  focus,
  children,
}) => {
  const { t } = useTranslation(["common", "events"]);
  const { isMobile } = useAppEnvironment();
  const { active, ...cameraRest } = camera;
  const focused = focus ?? (isMobile && scanning);
  const [typingOpen, setTypingOpen] = useState(false);
  const fit = useFitToWindow(focused && layout === "console");

  // Sound needs a tap first, and this listens from the moment the page opens: the tap that starts the camera is that tap.
  useEffect(() => unlockScanSound(), []);

  useEffect(() => {
    if (!scanning) {
      setTypingOpen(false);
    }
  }, [scanning]);

  const scanBar = (
    <Stack gap={focused ? "xs" : "sm"}>
      {!focused || typingOpen ? input : null}
      {scanning ? (
        <ScanCamera
          active={active ?? true}
          {...cameraRest}
          banner={banner}
          counters={focused ? counters : null}
          typing={
            focused && input
              ? {
                  open: typingOpen,
                  onToggle: () => setTypingOpen((open) => !open),
                  label: t("events:checkin.camera.typeCode", { defaultValue: "Type a code or a name" }),
                }
              : null
          }
        />
      ) : null}
      {banner && !banner.quiet ? <ScanResultBanner banner={banner} action={bannerAction} onClose={onBannerClose} compact={compactBanner} dense={focused} /> : null}
    </Stack>
  );

  // ⚠ Only a bar with something in it is pinned — an empty sticky box would still paint a band over the form.
  const hasScanBar = !!input || scanning || (!!banner && !banner.quiet);

  const pinnedBar =
    layout === "flow" && pinScanBar && hasScanBar ? (
      <Box
        style={{
          position: "sticky",
          top: pinTop,
          zIndex: 5,
          background: "var(--mantine-color-neutral-0, var(--mantine-color-body))",
          paddingBlock: "var(--mantine-spacing-xs)",
          marginBlock: "calc(var(--mantine-spacing-xs) * -1)",
        }}
      >
        {scanBar}
      </Box>
    ) : hasScanBar ? (
      scanBar
    ) : null;

  const body =
    layout === "console" ? (
      // ⚠ Never squeezed away on a short phone: below this the console itself scrolls (FOCUSED_BODY_MIN), so a person's card and
      // its buttons can always be reached with the camera on.
      <ScrollArea style={{ flex: 1, minHeight: focused ? FOCUSED_BODY_MIN : 0 }} type="auto">
        {/* ⚠⚠ BOTH WRAPPERS LIVE INSIDE THE SCROLL REGION, and that is a layout requirement, not a preference. The
            console is a flex column whose `height: 100%` lets this `ScrollArea` take `flex: 1` — so the code box and
            banner stay put while only the body scrolls. `PullToRefresh` forces `height: auto` on its own wrappers, so
            putting either around the whole page breaks that chain: the ScrollArea stops being a scroll region, the
            page grows to its content, and the scan input scrolls off the top of a phone mid-shift. */}
        <ContentLoadingOverlay loading={refreshing}>
          {onRefresh ? (
            <PullToRefresh onRefresh={onRefresh}>
              <Stack gap="sm" pb="sm">
                {children}
              </Stack>
            </PullToRefresh>
          ) : (
            <Stack gap="sm" pb="sm">
              {children}
            </Stack>
          )}
        </ContentLoadingOverlay>
      </ScrollArea>
    ) : (
      children
    );

  const showStatus = !focused && (!!status || (!!counters && counters.length > 0));

  return (
    <Stack
      ref={fit.ref}
      gap={focused ? "xs" : "sm"}
      style={layout === "console" ? { height: fit.height ? `calc(${fit.height}px - env(safe-area-inset-bottom))` : "100%", overflowY: focused ? "auto" : undefined } : undefined}
    >
      {mode}
      {context}
      {showStatus ? (
        <Group gap="xs" justify="space-between">
          {status}
          {counters && counters.length > 0 ? <ScanCounters items={counters} /> : null}
        </Group>
      ) : null}
      {pinnedBar}
      {error}
      {body}
      {hints}
    </Stack>
  );
};
